import {
    enrollAndUnlockLocalProtection,
    unlockLocalDataKeyFromEnrollment,
    type CredentialBinding,
    type EnrollAndUnlockLocalProtectionResult,
    type LocalCredentialRecord,
    type LocalEncryptionStore,
    type WrappedKeyRecord
} from '@tadpoles/local-encryption'
import {
    encodeBase64Url,
    getWebCredentialCapabilities,
    type AuthenticatorPrfInput
} from '@tadpoles/web-credentials'

import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

type CurrentLocalEncryptionContext = {
    binding: Extract<CredentialBinding, { kind: 'local-device' }>
    userId: string
}

type PreparedLocalEncryptionContext = CurrentLocalEncryptionContext & {
    credentialRecord: LocalCredentialRecord | null
    store: LocalEncryptionStore
    wrappedKeyRecord: WrappedKeyRecord | null
}

type LocalEncryptionIdleState = {
    phase: 'idle'
}

type LocalEncryptionRunningState = {
    phase: 'running'
}

type LocalEncryptionReadyState = {
    action: 'enrolled' | 'unlocked'
    keyAlgorithm: string
    phase: 'ready'
}

type LocalEncryptionErrorState = {
    errorMessage: string
    phase: 'error'
}

export type LocalEncryptionState =
    | LocalEncryptionIdleState
    | LocalEncryptionRunningState
    | LocalEncryptionReadyState
    | LocalEncryptionErrorState

export type LocalEncryptionPreparationState =
    | {
          phase: 'idle'
      }
    | {
          phase: 'preparing'
      }
    | {
          phase: 'ready'
      }
    | {
          errorMessage: string
          phase: 'error'
      }

const LocalDeviceId = 'family-app'
const LocalProtectionRpName = 'Tadpoles Family App'
const LocalProtectionSaltPrefix = 'tadpoles-family-local-protection'

export class LocalEncryptionService extends UserScopedService implements ServiceLifecycle {
    readonly #localBacking: FamilyUserLocalBacking
    #key: CryptoKey | undefined
    #operationVersion = 0
    #preparedContext: PreparedLocalEncryptionContext | undefined
    #preparationVersion = 0
    public preparation: LocalEncryptionPreparationState = $state({
        phase: 'idle'
    })
    public state: LocalEncryptionState = $state({
        phase: 'idle'
    })

    public constructor(scope: UserScopedServiceScope, localBacking: FamilyUserLocalBacking) {
        super(scope)
        this.#localBacking = localBacking
    }

    public get hasKey(): boolean {
        return this.#key !== undefined
    }

    public get isPrepared(): boolean {
        return this.preparation.phase === 'ready'
    }

    public async initialize(): Promise<void> {
        await this.prepare()
    }

    public async resume(): Promise<void> {
        await this.prepare()
    }

    public suspend(): void {
        this.reset()
    }

    public destroy(): void {
        this.reset()
    }

    public prepare = async (): Promise<void> => {
        const context = this.#getCurrentContext()

        if (
            this.#preparedContext !== undefined &&
            this.#preparedContext.binding.userId === context.binding.userId
        ) {
            this.preparation = {
                phase: 'ready'
            }
            return
        }

        const preparationVersion = this.#beginPreparation()
        this.preparation = {
            phase: 'preparing'
        }

        try {
            const store = await this.#localBacking.getLocalEncryptionStore()

            if (store === undefined) {
                if (this.#isCurrentPreparation(preparationVersion, context.userId)) {
                    this.#preparedContext = undefined
                    this.preparation = {
                        phase: 'idle'
                    }
                }
                return
            }

            const capabilities = await getWebCredentialCapabilities()
            this.#assertCapabilities(capabilities)
            const [credentialRecord, wrappedKeyRecord] = await Promise.all([
                store.getCredential(context.binding),
                store.getWrappedKey(context.binding)
            ])
            this.#assertPreparedRecords(credentialRecord, wrappedKeyRecord)

            if (!this.#isCurrentPreparation(preparationVersion, context.userId)) {
                return
            }

            this.#preparedContext = {
                ...context,
                credentialRecord,
                store,
                wrappedKeyRecord
            }
            this.preparation = {
                phase: 'ready'
            }
        } catch (error) {
            if (!this.#isCurrentPreparation(preparationVersion)) {
                return
            }

            this.#preparedContext = undefined
            this.preparation = {
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : 'Unable to prepare local encryption state.',
                phase: 'error'
            }
        }
    }

    public unlock = async (): Promise<void> => {
        const operationVersion = this.#beginOperation()

        this.state = {
            phase: 'running'
        }

        try {
            const context = this.#getCurrentContext()
            const store = await this.#localBacking.getLocalEncryptionStore()

            if (store === undefined) {
                throw new Error('Local encryption is unavailable in this security context.')
            }

            const preparedContext = this.#getPreparedContext(context)
            const unlockRequest = this.#createUnlockRequest()
            let enrollmentResult: EnrollAndUnlockLocalProtectionResult | undefined
            const key =
                preparedContext.credentialRecord === null
                    ? (enrollmentResult = await this.#enroll(
                          preparedContext.store,
                          context.binding,
                          unlockRequest
                      )).key
                    : await this.#unlockFromPreparedContext(preparedContext, unlockRequest)

            if (!this.#isCurrentOperation(operationVersion, context.userId)) {
                return
            }

            if (enrollmentResult !== undefined) {
                this.#preparedContext = {
                    ...preparedContext,
                    credentialRecord: enrollmentResult.credentialRecord,
                    wrappedKeyRecord: enrollmentResult.wrappedKeyRecord
                }
            }

            this.#key = key
            this.state = {
                action: preparedContext.credentialRecord === null ? 'enrolled' : 'unlocked',
                keyAlgorithm: this.#key.algorithm.name,
                phase: 'ready'
            }
        } catch (error) {
            if (!this.#isCurrentOperation(operationVersion)) {
                return
            }

            this.#key = undefined
            this.state = {
                errorMessage:
                    error instanceof Error ? error.message : 'Unable to unlock local encryption.',
                phase: 'error'
            }
        }
    }

    public reset = (): void => {
        this.#operationVersion += 1
        this.#preparationVersion += 1
        this.#preparedContext = undefined
        this.#key = undefined
        this.preparation = {
            phase: 'idle'
        }
        this.state = {
            phase: 'idle'
        }
    }

    #getCurrentContext(): CurrentLocalEncryptionContext {
        const userId = this.userContext.person.key

        return {
            binding: {
                kind: 'local-device',
                userId,
                deviceId: LocalDeviceId
            },
            userId
        }
    }

    #beginOperation(): number {
        this.#operationVersion += 1
        return this.#operationVersion
    }

    #beginPreparation(): number {
        this.#preparationVersion += 1
        return this.#preparationVersion
    }

    #isCurrentOperation(operationVersion: number, userId?: string): boolean {
        if (this.#operationVersion !== operationVersion) {
            return false
        }

        return userId === undefined || this.userContext.person.key === userId
    }

    #isCurrentPreparation(preparationVersion: number, userId?: string): boolean {
        if (this.#preparationVersion !== preparationVersion) {
            return false
        }

        return userId === undefined || this.userContext.person.key === userId
    }

    #getPreparedContext(context: CurrentLocalEncryptionContext): PreparedLocalEncryptionContext {
        if (
            this.#preparedContext === undefined ||
            this.#preparedContext.binding.userId !== context.binding.userId
        ) {
            throw new Error(
                'Local encryption is still preparing device state. Try again in a moment.'
            )
        }

        return this.#preparedContext
    }

    #getPreparedEnrollment(preparedContext: PreparedLocalEncryptionContext): {
        credentialRecord: LocalCredentialRecord
        wrappedKeyRecord: WrappedKeyRecord
    } {
        if (
            preparedContext.credentialRecord === null ||
            preparedContext.wrappedKeyRecord === null
        ) {
            throw new Error('Stored local encryption state is incomplete for this user.')
        }

        return {
            credentialRecord: preparedContext.credentialRecord,
            wrappedKeyRecord: preparedContext.wrappedKeyRecord
        }
    }

    #assertPreparedRecords(
        credentialRecord: LocalCredentialRecord | null,
        wrappedKeyRecord: WrappedKeyRecord | null
    ): void {
        if (credentialRecord === null && wrappedKeyRecord === null) {
            return
        }

        if (credentialRecord !== null && wrappedKeyRecord !== null) {
            return
        }

        throw new Error('Stored local encryption state is incomplete for this user.')
    }

    #assertCapabilities(
        capabilities: Awaited<ReturnType<typeof getWebCredentialCapabilities>>
    ): void {
        if (!capabilities.secureContext) {
            throw new Error('Local encryption requires a secure browser context.')
        }

        if (!capabilities.webAuthn) {
            throw new Error('This browser does not support WebAuthn.')
        }

        if (!capabilities.webCrypto) {
            throw new Error('This browser does not support WebCrypto.')
        }

        if (capabilities.platformAuthenticator === 'unavailable') {
            throw new Error('No platform authenticator is available in this browser.')
        }

        if (capabilities.prf === 'unavailable') {
            throw new Error('This browser or authenticator does not support WebAuthn PRF.')
        }
    }

    #createRegistrationRequest(userId: string): PublicKeyCredentialCreationOptionsJSON {
        return {
            attestation: 'none',
            authenticatorSelection: {
                authenticatorAttachment: 'platform',
                residentKey: 'required',
                userVerification: 'required'
            },
            challenge: LocalEncryptionService.#createRandomBase64Url(32),
            pubKeyCredParams: [{ alg: -7, type: 'public-key' as const }],
            rp: {
                name: LocalProtectionRpName
            },
            user: {
                displayName: userId,
                id: encodeBase64Url(new TextEncoder().encode(`family-local:${userId}`)),
                name: userId
            }
        }
    }

    #createUnlockRequest(): PublicKeyCredentialRequestOptionsJSON {
        return {
            challenge: LocalEncryptionService.#createRandomBase64Url(32),
            userVerification: 'required'
        }
    }

    async #enroll(
        store: LocalEncryptionStore,
        binding: Extract<CredentialBinding, { kind: 'local-device' }>,
        unlockRequest: PublicKeyCredentialRequestOptionsJSON
    ): Promise<EnrollAndUnlockLocalProtectionResult> {
        return await enrollAndUnlockLocalProtection(store, {
            binding,
            prf: LocalEncryptionService.#createUserVerificationSalt(binding.userId),
            registration: this.#createRegistrationRequest(binding.userId),
            unlock: unlockRequest
        })
    }

    async #unlockFromPreparedContext(
        preparedContext: PreparedLocalEncryptionContext,
        unlockRequest: PublicKeyCredentialRequestOptionsJSON
    ): Promise<CryptoKey> {
        return await unlockLocalDataKeyFromEnrollment(
            this.#getPreparedEnrollment(preparedContext),
            {
                binding: preparedContext.binding,
                prf: LocalEncryptionService.#createUserVerificationSalt(
                    preparedContext.binding.userId
                ),
                unlock: unlockRequest
            }
        )
    }

    static #createRandomBase64Url(byteLength: number): string {
        if (typeof crypto === 'undefined') {
            throw new Error('WebCrypto is not available in this environment.')
        }

        return encodeBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)))
    }

    static #createUserVerificationSalt(userId: string): AuthenticatorPrfInput {
        const encoder = new TextEncoder()
        const origin = typeof location === 'undefined' ? 'unknown-origin' : location.origin

        return {
            first: encodeBase64Url(
                encoder.encode(`${LocalProtectionSaltPrefix}:${origin}:${userId}`)
            )
        }
    }
}
